/**
 * LivePitchView — CON-1610. The public audience screen.
 *
 * Patrick 2026-08-10: "give me a URL where someone can watch the pitch live and
 * I remote control it including my rehearsal text etc."
 *
 * 🔴 THIS RENDERS OUTSIDE THE ADMIN LOGIN GATE (App.jsx). It is reached only
 * with the deck's secret token and shows only what the public endpoint returns —
 * published slides, no transcript, no editor, no other deck.
 *
 * It holds NO state about position: it asks the server which slide is showing,
 * every 800ms. That is why a tablet that drops off the wifi mid-rehearsal just
 * catches up on its own — there is no event stream it can have missed.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SlideCanvas from './SlideCanvas';

const API = (process.env.REACT_APP_BACKEND_URL || 'https://core.honestlee.app').replace(/\/$/, '');

export default function LivePitchView() {
  const token = new URLSearchParams(window.location.search).get('t') || '';
  const [data, setData] = useState(null);
  const [liveId, setLiveId] = useState(null);
  const [liveVariant, setLiveVariant] = useState(0);   // CON-1641
  // The poll closes over `data`; a ref keeps it current without restarting the interval.
  const dataRef = useRef(null);
  const [err, setErr] = useState(null);
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [stale, setStale] = useState(false);
  const [deckId, setDeckId] = useState(null);
  const missed = useRef(0);

  // full payload — once
  useEffect(() => {
    if (!token) { setErr('This link is missing its code.'); return; }
    let alive = true;
    fetch(`${API}/api/pitch/live/${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(j => {
        if (!alive) return;
        if (j.success === false) { setErr('This link is not active.'); return; }
        setData(j);
        setDeckId(j.deck_id ?? null);
        setLiveId(j.live_slide_id ? Number(j.live_slide_id) : (j.slides?.[0]?.id ?? null));
        // CON-1641 — which sub-slide the presenter is on. 0 = the slide's own image.
        setLiveVariant(Number(j.live_variant) || 0);
      })
      .catch(() => alive && setErr('Could not reach the server.'));
    return () => { alive = false; };
  }, [token]);

  useEffect(() => { dataRef.current = data; }, [data]);

  // heartbeat — which slide is showing, and is anybody presenting at all
  const [live, setLive] = useState(null);   // { live_mode, live_at, live_started_at }
  useEffect(() => {
    if (!token || !data) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/api/pitch/live/${encodeURIComponent(token)}/pos`);
        const j = await r.json();
        if (!alive) return;
        if (j.success) {
          // 🔴 FOLLOW WHATEVER DECK IS BEING PRESENTED. The heartbeat now carries
          // deck_id; when it differs from the payload we hold, the presenter has
          // switched deck (V3 -> V4, or a version that did not exist when this
          // screen was opened) and we pull the new slides. Without this the viewer
          // would keep rendering the old deck's slides against the new deck's ids
          // and simply stop advancing — which is exactly how it looked broken.
          if (j.deck_id != null && deckId != null && String(j.deck_id) !== String(deckId)) {
            const full = await fetch(`${API}/api/pitch/live/${encodeURIComponent(token)}`).then(x => x.json());
            if (full.success !== false) { setData(full); setDeckId(full.deck_id ?? null); }
          }
          if (j.live_slide_id) setLiveId(Number(j.live_slide_id));
          /* 🔴 CON-1641b — SELF-HEAL A STALE PAYLOAD.
           *
           * `data` is fetched once and then only refetched when the DECK changes,
           * so an audience screen opened before sub-slides existed holds slides
           * with no `variants` array and can never show one — the heartbeat says
           * "sub-slide 2" and this screen has nothing to render for it, forever,
           * with no error and no way to notice. That is a projector showing the
           * wrong thing during a pitch.
           *
           * If the presenter is on a sub-slide and the slide we hold has no
           * variants, our copy predates them: pull the deck again. Guarded on
           * both conditions so it fires once after a deploy, not every tick. */
          const vIdx = Number(j.live_variant) || 0;
          if (vIdx > 0) {
            const held = (dataRef.current?.slides || []).find(
              (x) => Number(x.id) === Number(j.live_slide_id));
            if (held && !(held.variants || []).length) {
              const full = await fetch(`${API}/api/pitch/live/${encodeURIComponent(token)}`)
                .then(x => x.json()).catch(() => null);
              if (full && full.success !== false) { setData(full); setDeckId(full.deck_id ?? null); }
            }
          }
          /* 🔴 Set UNCONDITIONALLY. Guarding this the way live_slide_id is
             guarded would make it impossible to step BACK to the base image,
             because 0 is falsy — the projector would stick on the last
             sub-slide for the rest of the deck. */
          setLiveVariant(Number(j.live_variant) || 0);
          setLive(j);
        }
        missed.current = 0;
        setStale(false);
      } catch {
        // Keep showing the current slide. Only admit to a problem after several
        // consecutive failures — a single dropped poll on conference wifi is
        // normal and flashing a warning at the audience would be worse than
        // silently holding the slide.
        missed.current += 1;
        if (missed.current > 6) setStale(true);
      }
    };
    tick();
    const iv = setInterval(tick, 800);
    return () => { alive = false; clearInterval(iv); };
  }, [token, data, deckId]);

  // A ticking value so the clock and the liveness window re-evaluate every second
  // without the poll having to drive the render.
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 500); return () => clearInterval(iv); }, []);

  useEffect(() => {
    const r = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', r);
    window.addEventListener('orientationchange', r);
    return () => { window.removeEventListener('resize', r); window.removeEventListener('orientationchange', r); };
  }, []);

  // Keep the screen awake — a rehearsal is minutes of no touch input, and a
  // tablet dimming mid-pitch is the most obvious way this feature looks broken.
  useEffect(() => {
    let lock = null, released = false;
    const grab = async () => {
      try { if ('wakeLock' in navigator) lock = await navigator.wakeLock.request('screen'); } catch { /* unsupported */ }
    };
    grab();
    const onVis = () => { if (document.visibilityState === 'visible' && !released) grab(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { released = true; document.removeEventListener('visibilitychange', onVis); lock?.release?.().catch(() => {}); };
  }, []);

  // The deck theme comes from the HEARTBEAT when present, so switching style in
  // the presenter header restyles this screen within a second — no reload.
  const themeFor = useCallback((s) => {
    if (!data) return null;
    const key = s?.theme_key || live?.theme_key || data.deck?.theme_key;
    return data.themes?.find(t => t.key === key) || data.themes?.[0] || null;
  }, [data, live]);

  const box = { position: 'fixed', inset: 0, background: '#000', display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: '#94a3b8',
                fontFamily: 'system-ui, sans-serif', textAlign: 'center', padding: 24 };

  if (err) return <div style={box}><div><div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>{err}</div></div>;
  if (!data) return <div style={box}>Connecting…</div>;

  // 🔴 LIVENESS IS A RECENT HEARTBEAT, NOT A FLAG. The presenter checks in every
  // 5s while rehearsing, so a locked phone or a closed tab drops this screen back
  // to Offline by itself. Patrick 2026-08-10: "it should start with a blank screen
  // pitch deck Offline or something indicating that its not live and only when I
  // start rehearsing it goes live."
  const beat = live?.live_at ? new Date(live.live_at).getTime() : 0;
  const isLive = live?.live_mode && live.live_mode !== 'off' && beat && (now - beat) < 15000;

  if (!isLive) {
    return (
      <div style={{ ...box, background: '#07090d', flexDirection: 'column', gap: 14 }}>
        <div style={{ width: 12, height: 12, borderRadius: 99, background: '#334155' }} />
        <div style={{ fontSize: 22, fontWeight: 700, color: '#64748b', letterSpacing: '0.02em' }}>
          Pitch deck offline
        </div>
        <div style={{ fontSize: 13, color: '#3f4a5c', maxWidth: 340, lineHeight: 1.5 }}>
          This screen goes live the moment the presenter starts rehearsing. Leave it open.
        </div>
      </div>
    );
  }

  // 🔴 COMPARE AS NUMBERS. Postgres serialises bigint as a STRING in JSON, so
  // slide.id arrives as "3" while liveId is Number()'d to 3 — a === comparison
  // NEVER matches, find() falls through to slides[0], and the audience screen
  // shows slide 1 forever. This was the whole "next slide does nothing" bug:
  // the theme and the clock kept working because neither involves an id.
  const slide = data.slides.find(s => Number(s.id) === Number(liveId)) || data.slides[0];
  if (!slide) return <div style={box}>This deck has no published slides yet.</div>;

  // fit 16:9 inside whatever screen this is, portrait phone or 4:3 TV included
  const w = Math.min(size.w, (size.h * 16) / 9);
  const hh = w * 9 / 16;
  const barY = Math.round((size.h - hh) / 2);
  const barX = Math.round((size.w - w) / 2);

  // The clock is OFF by default and only ever appears in rehearsal. On stage a
  // visible countdown tells the judges you are worried about time and makes them
  // watch the clock instead of you.
  const clockOn = live?.show_viewer_clock ?? data.deck?.show_viewer_clock;
  const showClock = clockOn && live?.live_started_at && live.live_mode === 'rehearsal';
  const runSecs = showClock ? Math.max(0, Math.round((now - new Date(live.live_started_at).getTime()) / 1000)) : 0;
  const target = data.deck?.target_seconds || 255;
  const mmssv = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      <SlideCanvas slide={slide} theme={themeFor(slide)} deck={data.deck} width={w} variantIndex={liveVariant} />
      {/* The true 16:9 boundary, so on a 4:3 TV or an ultrawide monitor you can
          see exactly where the slide ends and the letterbox begins. */}
      {barY > 1 ? (<>
        <div style={{ position: 'fixed', left: 0, right: 0, top: barY, height: 1, background: '#ffffff1f' }} />
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: barY, height: 1, background: '#ffffff1f' }} />
      </>) : null}
      {barX > 1 ? (<>
        <div style={{ position: 'fixed', top: 0, bottom: 0, left: barX, width: 1, background: '#ffffff1f' }} />
        <div style={{ position: 'fixed', top: 0, bottom: 0, right: barX, width: 1, background: '#ffffff1f' }} />
      </>) : null}
      {showClock ? (
        <div style={{ position: 'fixed', top: 14, left: 14, padding: '6px 12px', borderRadius: 8,
                      background: runSecs > target ? '#dc2626' : '#0f172acc', color: '#fff',
                      fontFamily: 'system-ui', fontSize: 20, fontWeight: 800,
                      fontVariantNumeric: 'tabular-nums' }}>
          {mmssv(runSecs)} <span style={{ fontSize: 12, opacity: 0.6 }}>/ {mmssv(target)}</span>
        </div>
      ) : null}
      {stale ? (
        <div style={{ position: 'fixed', top: 12, right: 12, background: '#7f1d1d', color: '#fecaca',
                      padding: '6px 12px', borderRadius: 8, fontSize: 12, fontFamily: 'system-ui' }}>
          reconnecting…
        </div>
      ) : null}
    </div>
  );
}
